import { useEffect } from "react";
import { SITE_URL } from "@/lib/siteConfig";

export interface UseCanonicalOptions {
  /** Path after the canonical origin, e.g. "/privacy" or "/blog/post-slug". */
  path: string;
  /**
   * Optional origin override. Defaults to SITE_URL (https://myelinahealth.com).
   * Only override for non-canonical environments when absolutely necessary.
   */
  origin?: string;
}

/**
 * Sets a self-referencing rel="canonical" link for the current route.
 * Restores the previous canonical href on unmount so route changes don't
 * leak one page's canonical URL into another.
 */
export function useCanonical({ path, origin = SITE_URL }: UseCanonicalOptions) {
  useEffect(() => {
    const normalizedOrigin = origin.replace(/\/+$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const href = `${normalizedOrigin}${normalizedPath}`;

    const existing = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const element = existing ?? document.createElement("link");
    const previous = existing?.getAttribute("href") ?? null;

    if (!existing) {
      element.setAttribute("rel", "canonical");
      document.head.appendChild(element);
    }

    element.setAttribute("href", href);

    return () => {
      if (!existing) {
        element.remove();
      } else if (previous === null) {
        element.removeAttribute("href");
      } else {
        element.setAttribute("href", previous);
      }
    };
  }, [path, origin]);
}

export default useCanonical;
